import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface Row {
  id: string;
  name: string;
  manufacturer: string | null;
  image: string | null;
  categories: string[] | null;
  sort_order: number | null;
  is_featured: boolean;
}

const TOP_CATEGORIES: Array<{ slug: string; label: string }> = [
  { slug: 'sexual-health', label: 'Sexual Health' },
  { slug: 'hair-loss', label: 'Hair Loss' },
  { slug: 'heart-health', label: 'Heart Health' },
  { slug: 'diabetes', label: 'Diabetes' },
  { slug: 'diet-health', label: 'Weight / Diet' },
  { slug: 'acne-skin', label: 'Acne / Skin' },
  { slug: 'antibiotics', label: 'Antibiotics' },
  { slug: 'antiparasitic', label: 'Antiparasitic' },
  { slug: 'respiratory', label: 'Respiratory' },
  { slug: 'hormones', label: 'Hormones' },
  { slug: 'beauty-etc', label: 'Beauty / Other' },
];

// Extract active ingredient from product name (inside parens), strip dosage/unit
function extractIngredient(name: string): string | null {
  if (!name) return null;
  const m = name.match(/\(([^)]+)\)/);
  if (!m) return null;
  return m[1]
    .replace(/\s*\d+(\.\d+)?\s*(mg|mcg|g|%|ml|iu)\b.*/gi, '')
    .trim();
}

type ViewMode = 'category' | 'ingredient';

const AdminProductPicker: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('category');

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, manufacturer, image, categories, sort_order, is_featured')
        .order('name');
      if (error) console.error(error);
      setRows((data as any[]) || []);
      setLoading(false);
    })();
  }, []);

  const featuredCount = rows.filter(r => r.is_featured).length;

  // Group by top-level category
  const groupedByCategory = useMemo(() => {
    const result: Array<{ slug: string; label: string; products: Row[] }> = [];
    const seen = new Set<string>();
    for (const cat of TOP_CATEGORIES) {
      const inCat = rows.filter(r => (r.categories || []).includes(cat.slug));
      inCat.forEach(r => seen.add(r.id));
      if (inCat.length > 0) result.push({ ...cat, products: inCat });
    }
    const orphans = rows.filter(r => !seen.has(r.id));
    if (orphans.length > 0) {
      result.push({ slug: '_other', label: 'Other / Uncategorized', products: orphans });
    }
    return result;
  }, [rows]);

  // Group by active ingredient, sorted by product count (most brands first)
  const groupedByIngredient = useMemo(() => {
    const map = new Map<string, Row[]>();
    const noIng: Row[] = [];
    rows.forEach(r => {
      const ing = extractIngredient(r.name);
      if (!ing) {
        noIng.push(r);
        return;
      }
      const arr = map.get(ing) || [];
      arr.push(r);
      map.set(ing, arr);
    });
    const entries = Array.from(map.entries())
      .map(([label, products]) => ({
        slug: label.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        label,
        products,
      }))
      .sort((a, b) => b.products.length - a.products.length || a.label.localeCompare(b.label));
    if (noIng.length > 0) {
      entries.push({ slug: '_no-ingredient', label: 'Unclassified', products: noIng });
    }
    return entries;
  }, [rows]);

  const grouped = viewMode === 'category' ? groupedByCategory : groupedByIngredient;

  const toggle = async (r: Row) => {
    const newVal = !r.is_featured;
    setRows(prev => prev.map(x => x.id === r.id ? { ...x, is_featured: newVal } : x));
    setSavingIds(prev => new Set(prev).add(r.id));
    const { error } = await supabase
      .from('products')
      .update({ is_featured: newVal })
      .eq('id', r.id);
    setSavingIds(prev => {
      const next = new Set(prev);
      next.delete(r.id);
      return next;
    });
    if (error) {
      setRows(prev => prev.map(x => x.id === r.id ? { ...x, is_featured: !newVal } : x));
      alert(`Failed: ${error.message}`);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading products…</div>;

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4 sticky top-0 bg-white z-20 py-4 border-b">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Picker</h1>
          <p className="text-sm text-gray-500 mt-1">
            Click a card to toggle "Featured". Featured products take priority on the homepage.
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          {/* View mode toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('category')}
              className={`px-3 py-1.5 text-sm rounded-md transition ${
                viewMode === 'category'
                  ? 'bg-white shadow text-gray-900 font-medium'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Category
            </button>
            <button
              onClick={() => setViewMode('ingredient')}
              className={`px-3 py-1.5 text-sm rounded-md transition ${
                viewMode === 'ingredient'
                  ? 'bg-white shadow text-gray-900 font-medium'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Ingredient
            </button>
          </div>
          <span className="text-gray-600">
            Featured: <strong className="text-green-700">{featuredCount}</strong> / {rows.length}
          </span>
        </div>
      </div>

      {/* Table of contents */}
      <nav className="flex flex-wrap gap-2 mb-8 text-xs">
        {grouped.map(g => (
          <a
            key={g.slug}
            href={`#grp-${g.slug}`}
            className="px-3 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700"
          >
            {g.label} <span className="text-gray-400">({g.products.length})</span>
          </a>
        ))}
      </nav>

      {/* Sections */}
      {grouped.map(g => {
        const grpFeatured = g.products.filter(p => p.is_featured).length;
        return (
          <section key={g.slug} id={`grp-${g.slug}`} className="mb-12 scroll-mt-24">
            <div className="flex items-baseline gap-3 mb-4 pb-2 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">{g.label}</h2>
              <span className="text-sm text-gray-500">{g.products.length} products</span>
              {grpFeatured > 0 && (
                <span className="text-sm text-green-700">• {grpFeatured} featured</span>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {g.products.map(r => {
                const isSaving = savingIds.has(r.id);
                const ing = extractIngredient(r.name) || r.manufacturer || '';
                return (
                  <button
                    key={r.id}
                    onClick={() => toggle(r)}
                    disabled={isSaving}
                    className={`relative text-left rounded-lg border-2 transition-all overflow-hidden ${
                      r.is_featured
                        ? 'border-green-500 ring-2 ring-green-200 bg-green-50/40'
                        : 'border-gray-200 hover:border-gray-400 bg-white'
                    } ${isSaving ? 'opacity-60' : ''}`}
                  >
                    {r.is_featured && (
                      <div className="absolute top-2 right-2 z-10 bg-green-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                        ★ Featured
                      </div>
                    )}
                    <div className="aspect-square bg-white flex items-center justify-center">
                      {r.image ? (
                        <img
                          src={r.image}
                          alt={r.name}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <div className="text-gray-300 text-xs">No image</div>
                      )}
                    </div>
                    <div className="p-3 border-t border-gray-100">
                      <div className="text-xs text-gray-500 truncate">{ing}</div>
                      <div className="text-sm font-medium text-gray-900 line-clamp-2 mt-0.5" title={r.name}>
                        {r.name.replace(/\s*\([^)]+\)\s*$/, '')}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default AdminProductPicker;
